import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Search, Filter, Copy, Check } from "lucide-react";
import UserDetailPopup from "../../../ui/UserDetailPopup";
import { API_URLS } from "../../../api/urls";
import AuthUtils from "../../../utils/auth.utils";

// Define types for the data structure
interface User {
  user_id: number;
  name: string;
  role: string;
  email: string;
  joined: string;
  status: "Active" | "In-active" | "DNS";
  profile_image: string | null;
  avatar: string;
  company?: string;
  projectsCreated?: number;
  feedbackCreated?: number;
  screenshots?: number;
  recordings?: number;
  lastActive?: string;
}

// Define type for detailed user data from API
interface UserDetails {
  user_id: number;
  name: string;
  role: string;
  email: string;
  profile_image: string | null;
  workspace_name: string;
  total_cards_created: number;
  total_feedback: number;
  total_screenshots: number;
  total_recordings: number;
  last_active: string;
  joined?: string;
  status?: "Active" | "In-active" | "DNS";
  avatar?: string;
}

const UsersPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [usersCache, setUsersCache] = useState<{ [key: number]: User[] }>({});
  const [loading, setLoading] = useState(true);
  const [popupLoading, setPopupLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);
  const rowsPerPage = 10;

  // Format date to match the existing format
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
  };

  // Get profile image URL helper
  const getProfileImage = (profileImage: string | null) => {
    if (!profileImage) return null;
    // If it's already a full URL, return it
    if (profileImage.startsWith('http')) return profileImage;
    // Otherwise, prepend the base URL
    return `https://api.feebdack.in${profileImage}`;
  };

  // Generate avatar emoji based on user name
  const getAvatarEmoji = (name: string) => {
    const emojis = ['👨‍💼', '👩‍💼', '👨‍🦰', '👩‍🦰', '👨‍🦱', '👩‍🦱', '👨', '👩'];
    const index = name.charCodeAt(0) % emojis.length;
    return emojis[index];
  };

  // Copy email to clipboard
  const handleCopyEmail = async (email: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    try {
      await navigator.clipboard.writeText(email);
      setCopiedEmail(email);
      setTimeout(() => setCopiedEmail(null), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };

  // Fetch users from API with caching
  const fetchUsers = async (page: number, isBackground = false) => {
    try {
      // Only show loading for foreground requests
      if (!isBackground) {
        setLoading(true);
      }
      
      // Check if page is already cached
      if (usersCache[page]) {
        setUsers(usersCache[page]);
        if (!isBackground) {
          setLoading(false);
        }
        return;
      }

      setError(null);

      // Get admin_id from session
      const adminId = AuthUtils.getAdminId();
      
      if (!adminId) {
        setError('Session expired. Please login again.');
        setLoading(false);
        return;
      }

      const response = await fetch(
        API_URLS.GET_ALL_USER_LIST(Number(adminId), page, rowsPerPage)
      );
      const apiData = await response.json();

      if (apiData.success === 1) {
        const formattedUsers = apiData.users.map((user: any) => ({
          user_id: user.user_id,
          name: user.name,
          role: user.role,
          email: user.email,
          joined: formatDate(user.joined),
          status: user.status,
          profile_image: getProfileImage(user.profile_image),
          avatar: getAvatarEmoji(user.name),
          company: user.company || "myworks",
          projectsCreated: user.projectsCreated || 0,
          feedbackCreated: user.feedbackCreated || 0,
          screenshots: user.screenshots || 0,
          recordings: user.recordings || 0,
          lastActive: user.lastActive ? formatDate(user.lastActive) : formatDate(user.joined)
        }));

        // Cache the page data
        setUsersCache(prev => ({ ...prev, [page]: formattedUsers }));
        
        if (!isBackground) {
          setUsers(formattedUsers);
        }
        
        setTotalUsers(apiData.total);
      } else {
        if (!isBackground) {
          setError('Failed to load users');
        }
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      if (!isBackground) {
        setError('Error loading users. Please try again.');
      }
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  };

  // Prefetch adjacent pages
  const prefetchAdjacentPages = (page: number, totalPages: number) => {
    // Prefetch next page
    if (page < totalPages && !usersCache[page + 1]) {
      fetchUsers(page + 1, true);
    }
    // Prefetch previous page
    if (page > 1 && !usersCache[page - 1]) {
      fetchUsers(page - 1, true);
    }
  };

  // Fetch user details for popup
  const fetchUserDetails = async (userId: number) => {
    try {
      setPopupLoading(true);
      const response = await fetch(API_URLS.GET_USER_DETAILS(userId));
      const apiData = await response.json();

      if (apiData.success === 1) {
        const userDetails: UserDetails = {
          user_id: apiData.user.user_id,
          name: apiData.user.name,
          email: apiData.user.email,
          role: apiData.user.role,
          profile_image: getProfileImage(apiData.user.profile_image),
          workspace_name: apiData.user.workspace_name,
          total_cards_created: apiData.stats.total_cards_created,
          total_feedback: apiData.stats.total_feedback,
          total_screenshots: apiData.stats.total_screenshots,
          total_recordings: apiData.stats.total_recordings,
          last_active: formatDate(apiData.stats.last_active),
          avatar: getAvatarEmoji(apiData.user.name)
        };
        
        setSelectedUser(userDetails);
      } else {
        console.error('Failed to load user details');
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
    } finally {
      setPopupLoading(false);
    }
  };

  // Load users when component mounts only once
  useEffect(() => {
    fetchUsers(1); // Start with page 1
  }, []);

  // Prefetch adjacent pages when current page or users change
  useEffect(() => {
    if (users.length > 0 && !searchQuery) {
      const totalPages = Math.ceil(totalUsers / rowsPerPage);
      prefetchAdjacentPages(currentPage, totalPages);
    }
  }, [currentPage, users, totalUsers, searchQuery]);

  // Filter users based on search query
  const filteredUsers = searchQuery
    ? users.filter(
        (user) =>
          user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.status.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : users;

  // Calculate pagination based on filtered results
  const totalPages = searchQuery 
    ? Math.ceil(filteredUsers.length / rowsPerPage)
    : Math.ceil(totalUsers / rowsPerPage);

  // For search, paginate filtered results. Otherwise use cached users
  const paginatedFilteredUsers = searchQuery
    ? filteredUsers.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
    : users;

  const handlePageChange = async (page: number) => {
    setCurrentPage(page);
    
    // If page is cached, load instantly. Otherwise fetch it.
    if (usersCache[page]) {
      setUsers(usersCache[page]);
    } else {
      await fetchUsers(page);
    }
  };

  const handleUserClick = async (user: User) => {
    // Set initial data from the table row
    setSelectedUser({
      user_id: user.user_id,
      name: user.name,
      email: user.email,
      role: user.role,
      profile_image: user.profile_image,
      workspace_name: user.company || "myworks",
      total_cards_created: user.projectsCreated || 0,
      total_feedback: user.feedbackCreated || 0,
      total_screenshots: user.screenshots || 0,
      total_recordings: user.recordings || 0,
      last_active: user.lastActive || "",
      avatar: user.avatar
    });
    setIsPopupOpen(true);
    
    // Fetch detailed data in background
    await fetchUserDetails(user.user_id);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedUser(null);
  };

  // Handle search change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
    // Restore cached data for current page
    if (usersCache[1]) {
      setUsers(usersCache[1]);
    }
  };

  const displayTotal = searchQuery ? filteredUsers.length : totalUsers;

  return (
    <div className="h-screen flex flex-col bg-white">
      {/* Fixed Header Section */}
      <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 pt-4 sm:pt-5 lg:pt-5 pb-2">
        {/* Error Banner */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
            <p className="text-red-600">{error}</p>
            <button 
              onClick={() => fetchUsers(currentPage)} 
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
            >
              Retry
            </button>
          </div>
        )}

        {/* Header with Search Bar */}
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-xl sm:text-2xl font-semibold text-black whitespace-nowrap">
            All users ({displayTotal})
          </h1>
          
          <div className="flex items-center gap-4">
            <button className="hover:bg-gray-100 p-2 rounded-lg transition-colors">
              <Filter className="w-5 h-5 text-gray-600" />
            </button>
            <div className="relative w-64">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search by name, email, role, or status"
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg "
              />
              {searchQuery && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table Section with Fixed Header */}
      <div className="flex-1 px-4 sm:px-6 lg:px-8 py-2 flex flex-col overflow-hidden">
        <div className="flex-1 bg-white rounded-3xl border border-[#CFCFCF] overflow-hidden flex flex-col relative">
          {/* Loading overlay only for initial load */}
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading users...</p>
              </div>
            </div>
          )}
          
          {/* Fixed Table Header */}
          <div className="flex-shrink-0 overflow-x-auto bg-[#F3F5F8] border-b border-[#CFCFCF]">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr>
                  <th className="text-left py-3 sm:py-4 px-4 sm:px-6 font-semibold text-gray-900 text-sm sm:text-base w-[30%]">
                    Name
                  </th>
                  <th className="text-left py-3 sm:py-4 px-4 sm:px-6 font-semibold text-gray-900 text-sm sm:text-base w-[15%]">
                    Role
                  </th>
                  <th className="text-left py-3 sm:py-4 px-4 sm:px-6 font-semibold text-gray-900 text-sm sm:text-base w-[25%]">
                    Email
                  </th>
                  <th className="text-left py-3 sm:py-4 px-4 sm:px-6 font-semibold text-gray-900 text-sm sm:text-base w-[15%]">
                    Joined
                  </th>
                  <th className="text-left py-3 sm:py-4 px-4 sm:px-6 font-semibold text-gray-900 text-sm sm:text-base w-[15%]">
                    Status
                  </th>
                </tr>
              </thead>
            </table>
          </div>

          {/* Scrollable Table Body */}
          <div className="flex-1 overflow-x-auto overflow-y-auto">
            <table className="w-full min-w-[640px]">
              <tbody className="bg-white">
                {paginatedFilteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-500">
                      {searchQuery ? 'No users found matching your search' : 'No users available'}
                    </td>
                  </tr>
                ) : (
                  paginatedFilteredUsers.map((user, index) => (
                    <tr
                      key={user.user_id}
                      onClick={() => handleUserClick(user)}
                      className={`${
                        index !== paginatedFilteredUsers.length - 1
                          ? "border-b border-gray-100"
                          : ""
                      } hover:bg-gray-50 transition-colors cursor-pointer`}
                    >
                      <td className="py-2 sm:py-2 px-4 sm:px-6 w-[30%]">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-orange-200 to-orange-300 flex items-center justify-center text-xl sm:text-2xl flex-shrink-0 overflow-hidden">
                            {user.profile_image ? (
                              <img 
                                src={user.profile_image} 
                                alt={user.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  // Fallback to emoji if image fails to load
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.parentElement!.textContent = user.avatar || '👤';
                                }}
                              />
                            ) : (
                              user.avatar || '👤'
                            )}
                          </div>
                          <span className="font-semibold text-[#111C2D] text-sm sm:text-base lg:text-sm 2xl:text-base font-inter">
                            {user.name}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 sm:py-4 px-4 sm:px-6 text-[#111C2D] text-sm sm:text-base lg:text-sm 2xl:text-base font-inter w-[15%]">
                        {user.role}
                      </td>
                      <td className="py-3 sm:py-4 px-4 sm:px-6 w-[25%]">
                        <div className="flex items-center gap-2 group">
                          <span className="text-[#111C2D] text-sm sm:text-base lg:text-sm 2xl:text-base font-inter">
                            {user.email}
                          </span>
                          <button
                            onClick={(e) => handleCopyEmail(user.email, e)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 hover:bg-gray-200 rounded"
                            title="Copy email"
                          >
                            {copiedEmail === user.email ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-600" />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="py-3 sm:py-4 px-4 sm:px-6 text-[#111C2D] text-sm sm:text-base lg:text-sm 2xl:text-base font-inter w-[15%]">
                        {user.joined}
                      </td>
                      <td className="py-3 sm:py-4 px-4 sm:px-6 w-[15%]">
                        <span
                          className={`inline-flex items-center px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium border ${
                            user.status === "Active"
                              ? "text-[#1EAB1E] border-[#1EAB1E]"
                              : user.status === "In-active"
                              ? "text-[#E2941F] border-[#E2941F]"
                              : "text-[#804BE9] border-[#804BE9]"
                          }`}
                        >
                          {user.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Fixed Pagination at Bottom */}
      <div className="flex-shrink-0 bg-white px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between border-t border-gray-100">
        <div className="text-sm text-gray-600">
          Rows per page: <span className="font-medium">{rowsPerPage}</span>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">
            {(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, displayTotal)} OF{" "}
            {displayTotal}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-1.5 rounded-lg transition-colors ${
                currentPage === 1
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`p-1.5 rounded-lg transition-colors ${
                currentPage === totalPages
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* User Detail Popup - Using the separate component */}
      <UserDetailPopup
        user={selectedUser}
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        loading={popupLoading}
      />
    </div>
  );
};

export default UsersPage;